import { useEffect, useRef, useState } from 'react';
import type { PDFDocumentProxy } from 'pdfjs-dist';
import workerUrl from 'pdfjs-dist/build/pdf.worker.min.mjs?url';

// Render the original protected bytes. No copies in public storage, localStorage or blobs.
export default function ScopeProtectedPDF() {
  const container = useRef<HTMLDivElement>(null);
  const canvas = useRef<HTMLCanvasElement>(null);
  const [pdf, setPdf] = useState<PDFDocumentProxy | null>(null);
  const [page, setPage] = useState(1);
  const [width, setWidth] = useState(700);
  const [error, setError] = useState('');
  const [rendered, setRendered] = useState(false);
  useEffect(() => {
    const node = container.current;
    if (!node) return;
    const observer = new ResizeObserver(entries => setWidth(Math.max(180, entries[0].contentRect.width)));
    observer.observe(node); return () => observer.disconnect();
  }, []);
  useEffect(() => {
    const controller = new AbortController();
    let disposed = false;
    let task: ReturnType<typeof import('pdfjs-dist').getDocument> | undefined;
    (async () => {
      const res = await fetch('/api/prisme/ce/scope-offres/document', { credentials: 'include', cache: 'no-store', signal: controller.signal });
      if (!res.ok) throw new Error('Le document n’est pas accessible. Vérifier votre session CE.');
      const bytes = new Uint8Array(await res.arrayBuffer());
      const lib = await import('pdfjs-dist');
      if (disposed) return;
      lib.GlobalWorkerOptions.workerSrc = workerUrl;
      task = lib.getDocument({ data: bytes, isEvalSupported: false });
      const document = await task.promise;
      if (disposed) { await document.destroy(); return; }
      setPdf(document);
    })().catch((err: Error) => { if (!disposed && err.name !== 'AbortError') setError(err.message); });
    return () => { disposed = true; controller.abort(); void task?.destroy(); };
  }, []);
  useEffect(() => {
    if (!pdf || !canvas.current) return;
    let disposed = false;
    let task: ReturnType<Awaited<ReturnType<PDFDocumentProxy['getPage']>>['render']> | undefined;
    setRendered(false);
    (async () => {
      const documentPage = await pdf.getPage(page);
      if (disposed || !canvas.current) return;
      const scale = Math.min(width, 1100) / documentPage.getViewport({ scale: 1 }).width;
      const viewport = documentPage.getViewport({ scale });
      const ratio = Math.min(window.devicePixelRatio || 1, 2);
      const node = canvas.current;
      node.width = viewport.width * ratio; node.height = viewport.height * ratio;
      node.style.width = `${viewport.width}px`; node.style.height = `${viewport.height}px`;
      const ctx = node.getContext('2d'); if (!ctx) throw new Error('Affichage PDF indisponible');
      task = documentPage.render({ canvasContext: ctx, viewport, transform: [ratio, 0, 0, ratio, 0, 0] });
      await task.promise;
      if (!disposed) setRendered(true);
    })().catch((err: Error) => { if (!disposed && err.name !== 'RenderingCancelledException') setError('Lecture PDF indisponible; utiliser le lien vers l’original.'); });
    return () => { disposed = true; task?.cancel(); };
  }, [pdf, page, width]);
  return <div ref={container} className="mt-5 min-w-0" aria-label="Lecteur du PDF original protégé">
    {error ? <p role="alert" className="rounded-xl bg-amber-50 p-4 text-sm text-amber-950">{error}</p> : <>
      <div className="mb-3 flex flex-wrap items-center justify-between gap-2 text-sm"><button disabled={!pdf || page <= 1} onClick={() => setPage(value => value - 1)} className="min-h-[44px] rounded-lg border border-slate-300 px-3 py-2 font-bold disabled:opacity-40">Page précédente</button><p role="status">{pdf ? `Page ${page} / ${pdf.numPages}${rendered ? '' : ' · chargement…'}` : 'Chargement du document protégé…'}</p><button disabled={!pdf || page >= pdf.numPages} onClick={() => setPage(value => value + 1)} className="min-h-[44px] rounded-lg border border-slate-300 px-3 py-2 font-bold disabled:opacity-40">Page suivante</button></div>
      <canvas ref={canvas} role="img" aria-label={`Page ${page} de l’offre originale`} data-rendered={rendered} className="mx-auto max-w-full rounded-lg border border-slate-200" />
      <p className="mt-3 text-xs text-slate-600">Le lien « Ouvrir dans un nouvel onglet » donne accès au PDF original, avec son texte sélectionnable et les commandes du lecteur de votre navigateur.</p>
    </>}
  </div>;
}
